import React, { useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import RuleCard from "./RuleCard";
import {
  RulesApi,
  RuleGroupDisplayDto,
} from "../../../Api/SocketRulesEngineApi.generated";
import { useOktaAuth } from "@okta/okta-react";
import {
  Button,
  Paper,
  Tooltip,
  TextField,
  Box,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useFlags } from "launchdarkly-react-client-sdk";

const Rules = ({ redirectPage }: any) => {
  const { oktaAuth } = useOktaAuth();
  const token = oktaAuth.getAccessToken();
  const rulesApi = new RulesApi();
  const launchDarklyFeatureFlag = useFlags();
  const [rules, setRules] = useState<RuleGroupDisplayDto[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const getRules = async () => {
    rulesApi.token = token;
    let rules = await rulesApi.getRuleGroups();
    return rules;
  };

  useEffect(() => {
    const fetchData = async () => {
      const rules = await getRules();
      setRules(rules);
    };
    fetchData();
  }, []);

  // Filter and search logic
  const filteredRules = useMemo(() => {
    let filtered = rules || [];

    // Apply search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (rule) =>
          rule.name?.toLowerCase().includes(lowerSearch) ||
          rule.description?.toLowerCase().includes(lowerSearch)
      );
    }

    // Apply status filter
    if (statusFilter === "active") {
      filtered = filtered.filter((rule) => rule.isActive);
    } else if (statusFilter === "inactive") {
      filtered = filtered.filter((rule) => rule.isInactive);
    } else if (statusFilter === "default") {
      filtered = filtered.filter((rule) => rule.isDefault);
    }

    return filtered;
  }, [rules, searchTerm, statusFilter]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = rules?.length || 0;
    const active = rules?.filter((r) => r.isActive).length || 0;
    const inactive = rules?.filter((r) => r.isInactive).length || 0;
    const defaultRules = rules?.filter((r) => r.isDefault).length || 0;
    return { total, active, inactive, defaultRules };
  }, [rules]);

  const handleStatusFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: string | null
  ) => {
    if (newFilter !== null) {
      setStatusFilter(newFilter);
    }
  };

  const newCard = filteredRules?.map((rule: RuleGroupDisplayDto) => {
    return (
      <Grid item xs={12} sx={{ mb: 3 }} key={rule.url}>
        <RuleCard rule={rule} redirectPage={redirectPage} />
      </Grid>
    );
  });

  return (
    <Grid item container xs={12} width="100%">
      <Paper
        elevation={10}
        sx={{
          padding: "40px 40px 30px 40px",
          width: "100%",
          minHeight: "calc(100vh - 120px)",
        }}
      >
        <Grid item container spacing={3}>
          {/* Header */}
          <Grid item container xs={12} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="h4">Rule Groups</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Manage and organize your rule groups
              </Typography>
            </Grid>
            {launchDarklyFeatureFlag["sru-create-rule-group-button"] && (
              <Grid item xs={12} sm={6} container justifyContent="flex-end">
                <Tooltip title="Create a new Rule Group" arrow>
                  <Button
                    variant="contained"
                    sx={{
                      borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      minWidth: "auto",
                    }}
                    onClick={() => {
                      redirectPage("/rule/new");
                    }}
                  >
                    <AddIcon />
                  </Button>
                </Tooltip>
              </Grid>
            )}
          </Grid>

          {/* Statistics Cards */}
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  textAlign: "center",
                  backgroundColor: "primary.main",
                  color: "white",
                }}
              >
                <Typography variant="h4">{stats.total}</Typography>
                <Typography variant="body2">Total Rule Groups</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  textAlign: "center",
                  backgroundColor: "success.main",
                  color: "white",
                }}
              >
                <Typography variant="h4">{stats.active}</Typography>
                <Typography variant="body2">Active</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  textAlign: "center",
                  backgroundColor: "warning.main",
                  color: "white",
                }}
              >
                <Typography variant="h4">{stats.inactive}</Typography>
                <Typography variant="body2">Inactive</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  textAlign: "center",
                  backgroundColor: "info.main",
                  color: "white",
                }}
              >
                <Typography variant="h4">{stats.defaultRules}</Typography>
                <Typography variant="body2">Default Rules</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Search and Filter Controls */}
          <Grid item container xs={12} spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search rule groups by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FilterListIcon color="action" />
                <ToggleButtonGroup
                  value={statusFilter}
                  exclusive
                  onChange={handleStatusFilterChange}
                  size="small"
                  sx={{ flexWrap: "wrap" }}
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="active">Active</ToggleButton>
                  <ToggleButton value="inactive">Inactive</ToggleButton>
                  <ToggleButton value="default">Default</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>
          </Grid>

          {/* Results Count */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredRules?.length} of {rules?.length} rule groups
              </Typography>
              {(searchTerm || statusFilter !== "all") && (
                <Button
                  size="small"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          </Grid>

          {/* Rule Cards */}
          {filteredRules?.length > 0 ? (
            newCard
          ) : (
            <Grid item xs={12}>
              <Paper
                elevation={1}
                sx={{
                  p: 4,
                  textAlign: "center",
                  backgroundColor: "grey.50",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  No rule groups found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {searchTerm || statusFilter !== "all"
                    ? "Try adjusting your filters"
                    : "Create a new rule group to get started"}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Grid>
  );
};

export default Rules;
